import React, { useState, useEffect } from 'react';
import { Layout, Row, Col, Card, Button, Select, Input, Space, Divider } from 'antd';
import { DndContext, closestCenter } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { BarChartOutlined, LineChartOutlined, PieChartOutlined, DotChartOutlined, TableOutlined } from '@ant-design/icons';
import DraggableField from './DraggableField';
import ChartPreview from './ChartPreview';
import DataSourceConnector from './DataSourceConnector';
import './ChartBuilder.css';

const { Header, Content, Sider } = Layout;
const { Option } = Select;

const ChartBuilder = () => {
  const [dataSource, setDataSource] = useState(null);
  const [fields, setFields] = useState([]);
  const [chartType, setChartType] = useState('bar');
  const [chartConfig, setChartConfig] = useState({
    xAxis: null,
    yAxis: null,
    color: null,
    size: null,
    filters: []
  });
  const [chartData, setChartData] = useState([]);

  const chartTypes = [
    { key: 'bar', label: 'Bar Chart', icon: <BarChartOutlined /> },
    { key: 'line', label: 'Line Chart', icon: <LineChartOutlined /> },
    { key: 'pie', label: 'Pie Chart', icon: <PieChartOutlined /> },
    { key: 'scatter', label: 'Scatter Plot', icon: <DotChartOutlined /> },
    { key: 'table', label: 'Table', icon: <TableOutlined /> }
  ];

  const handleDataSourceChange = (newDataSource, newFields) => {
    setDataSource(newDataSource);
    setFields(newFields);
    setChartConfig({
      xAxis: null,
      yAxis: null,
      color: null,
      size: null,
      filters: []
    });
  };

  const handleFieldDrop = (field, target) => {
    setChartConfig(prev => ({
      ...prev,
      [target]: field
    }));
  };

  const handleChartTypeChange = (type) => {
    setChartType(type);
    // Reset config when chart type changes
    setChartConfig({
      xAxis: null,
      yAxis: null,
      color: null,
      size: null,
      filters: []
    });
  };

  const generateChart = async () => {
    if (!dataSource || !chartConfig.xAxis) return;

    try {
      // This would connect to your custom API
      const response = await fetch('/api/chart/data', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          dataSource: dataSource.url,
          chartType,
          config: chartConfig
        })
      });
      
      const data = await response.json();
      setChartData(data);
    } catch (error) {
      console.error('Error generating chart:', error);
      // For demo purposes, generate mock data
      generateMockData();
    }
  };

  const generateMockData = () => {
    const mockData = [];
    for (let i = 0; i < 10; i++) {
      mockData.push({
        category: `Category ${i + 1}`,
        value: Math.floor(Math.random() * 100) + 10,
        group: Math.random() > 0.5 ? 'Group A' : 'Group B'
      });
    }
    setChartData(mockData);
  };

  useEffect(() => {
    if (chartConfig.xAxis && chartConfig.yAxis && chartType !== 'pie') {
      generateChart();
    } else if (chartType === 'pie' && chartConfig.xAxis) {
      generateChart();
    }
  }, [chartConfig, chartType]);

  return (
    <Layout style={{ height: '100vh' }}>
      <Header style={{ background: '#fff', padding: '0 24px', borderBottom: '1px solid #f0f0f0' }}>
        <Row justify="space-between" align="middle">
          <Col>
            <h2>Chart Builder</h2>
          </Col>
          <Col>
            <Space>
              <Button type="primary" onClick={generateChart}>
                Generate Chart
              </Button>
              <Button>Save</Button>
              <Button>Export</Button>
            </Space>
          </Col>
        </Row>
      </Header>

      <Layout>
        <Sider width={300} style={{ background: '#fafafa', padding: '16px' }}>
          <Card title="Data Source" size="small" style={{ marginBottom: 16 }}>
            <DataSourceConnector onDataSourceChange={handleDataSourceChange} />
          </Card>

          <Card title="Chart Type" size="small" style={{ marginBottom: 16 }}>
            <Row gutter={[8, 8]}>
              {chartTypes.map(type => (
                <Col span={12} key={type.key}>
                  <Button
                    block
                    type={chartType === type.key ? 'primary' : 'default'}
                    icon={type.icon}
                    onClick={() => handleChartTypeChange(type.key)}
                  >
                    {type.label}
                  </Button>
                </Col>
              ))}
            </Row>
          </Card>

          <Card title="Fields" size="small">
            <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
              {fields.map(field => (
                <DraggableField
                  key={field.name}
                  field={field}
                  onDrop={handleFieldDrop}
                />
              ))}
            </div>
          </Card>
        </Sider>

        <Layout>
          <Content style={{ padding: '16px' }}>
            <Row gutter={16} style={{ height: '100%' }}>
              <Col span={8}>
                <Card title="Chart Configuration" style={{ height: '100%' }}>
                  <Space direction="vertical" style={{ width: '100%' }}>
                    <div>
                      <label>X-Axis:</label>
                      <Select
                        style={{ width: '100%', marginTop: 4 }}
                        placeholder="Select field for X-axis"
                        value={chartConfig.xAxis?.name}
                        onChange={(value) => {
                          const field = fields.find(f => f.name === value);
                          handleFieldDrop(field, 'xAxis');
                        }}
                      >
                        {fields.map(field => (
                          <Option key={field.name} value={field.name}>
                            {field.name} ({field.type})
                          </Option>
                        ))}
                      </Select>
                    </div>

                    {chartType !== 'pie' && (
                      <div>
                        <label>Y-Axis:</label>
                        <Select
                          style={{ width: '100%', marginTop: 4 }}
                          placeholder="Select field for Y-axis"
                          value={chartConfig.yAxis?.name}
                          onChange={(value) => {
                            const field = fields.find(f => f.name === value);
                            handleFieldDrop(field, 'yAxis');
                          }}
                        >
                          {fields.filter(f => f.type === 'number').map(field => (
                            <Option key={field.name} value={field.name}>
                              {field.name} ({field.type})
                            </Option>
                          ))}
                        </Select>
                      </div>
                    )}

                    <div>
                      <label>Color By:</label>
                      <Select
                        style={{ width: '100%', marginTop: 4 }}
                        placeholder="Select field for color grouping"
                        value={chartConfig.color?.name}
                        onChange={(value) => {
                          const field = fields.find(f => f.name === value);
                          handleFieldDrop(field, 'color');
                        }}
                        allowClear
                      >
                        {fields.filter(f => f.type === 'string').map(field => (
                          <Option key={field.name} value={field.name}>
                            {field.name} ({field.type})
                          </Option>
                        ))}
                      </Select>
                    </div>

                    {chartType === 'scatter' && (
                      <div>
                        <label>Size By:</label>
                        <Select
                          style={{ width: '100%', marginTop: 4 }}
                          placeholder="Select field for bubble size"
                          value={chartConfig.size?.name}
                          onChange={(value) => {
                            const field = fields.find(f => f.name === value);
                            handleFieldDrop(field, 'size');
                          }}
                          allowClear
                        >
                          {fields.filter(f => f.type === 'number').map(field => (
                            <Option key={field.name} value={field.name}>
                              {field.name} ({field.type})
                            </Option>
                          ))}
                        </Select>
                      </div>
                    )}
                  </Space>
                </Card>
              </Col>

              <Col span={16}>
                <Card title="Chart Preview" style={{ height: '100%' }}>
                  <ChartPreview
                    type={chartType}
                    data={chartData}
                    config={chartConfig}
                  />
                </Card>
              </Col>
            </Row>
          </Content>
        </Layout>
      </Layout>
    </Layout>
  );
};

export default ChartBuilder;